import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from 'sonner';
import { FileText, Upload, Euro, CheckCircle, XCircle, AlertCircle, ShoppingCart, Wallet, Building, Star, TrendingUp, AlertTriangle, ScrollText, Clock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import DashboardHeader from '../components/DashboardHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ImpresaDashboard = () => {
  const { user, checkAuth } = useAuth();
  const [requests, setRequests] = useState([]);
  const [myCompany, setMyCompany] = useState(null);
  const [myDocuments, setMyDocuments] = useState([]);
  const [myReviews, setMyReviews] = useState({ reviews: [], avg_rating: 0, total: 0 });
  const [movements, setMovements] = useState({ recharges: [], purchases: [] });
  const [refunds, setRefunds] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [orderRequests, setOrderRequests] = useState([]);
  const [settings, setSettings] = useState(null);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [showRechargeModal, setShowRechargeModal] = useState(false);
  const [showOrderRequestModal, setShowOrderRequestModal] = useState(false);
  const [refundTarget, setRefundTarget] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const [r, comp, s, rv, mv, rf, po, oreq] = await Promise.all([
        axios.get(`${API}/renovation-requests`, { withCredentials: true }),
        axios.get(`${API}/companies/me`, { withCredentials: true }).catch(() => ({ data: { company: null, documents: [] } })),
        axios.get(`${API}/settings`, { withCredentials: true }),
        axios.get(`${API}/reviews/me`, { withCredentials: true }).catch(() => ({ data: { reviews: [], avg_rating: 0, total: 0 } })),
        axios.get(`${API}/movements/me`, { withCredentials: true }).catch(() => ({ data: { recharges: [], purchases: [] } })),
        axios.get(`${API}/refund-requests/me`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/purchase-orders/me`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/impresa/order-requests/me`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setRequests(r.data);
      setMyCompany(comp.data.company);
      setMyDocuments(comp.data.documents || []);
      setSettings(s.data);
      setMyReviews(rv.data);
      setMovements(mv.data);
      setRefunds(rf.data);
      setPurchaseOrders(po.data);
      setOrderRequests(oreq.data);
      await checkAuth();
    } catch (e) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  }, [checkAuth]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handlePurchase = async (req) => {
    const priceCents = (req.purchase_price_cents != null)
      ? req.purchase_price_cents
      : (settings?.request_purchase_price || 35) * 100;
    const priceEuro = (priceCents / 100).toFixed(2);
    if (!window.confirm(`Acquistare questa richiesta per €${priceEuro}?`)) return;
    try {
      await axios.post(`${API}/renovation-requests/${req.id}/purchase`, {}, { withCredentials: true });
      toast.success('Richiesta acquistata! Ora puoi vedere i dati del proprietario');
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore acquisto');
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#EA580C]" /></div>;
  }

  const balanceCents = (user?.credits_cents != null) ? user.credits_cents : (user?.credits || 0) * 100;
  const balanceEuro = (balanceCents / 100).toFixed(2);
  const verified = user?.verified;

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <DashboardHeader rightContent={
        <div className="flex items-center gap-3">
          <div className="bg-[#EA580C]/10 px-3 py-1.5 rounded-lg flex items-center gap-2" data-testid="credits-balance">
            <Wallet className="w-4 h-4 text-[#EA580C]" />
            <span className="font-semibold text-[#EA580C]">€{balanceEuro}</span>
          </div>
          <Button data-testid="recharge-btn" onClick={() => setShowRechargeModal(true)} size="sm" className="bg-[#EA580C] hover:bg-[#EA580C]/90 text-white rounded-lg">
            Ricarica
          </Button>
        </div>
      } />
      
      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-[#0F172A] mb-2">Area Impresa</h1>
          <p className="text-slate-600">Acquista richieste, gestisci documenti e i tuoi crediti</p>
        </div>

        {!verified && (
          <Card className="bg-yellow-50 border border-yellow-200 rounded-xl mb-6" data-testid="not-verified-alert">
            <CardContent className="p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold text-yellow-900">Account non ancora verificato</p>
                <p className="text-sm text-yellow-800">Carica tutti i documenti richiesti per essere verificato dall'admin. Solo dopo la verifica potrai acquistare richieste.</p>
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="requests" className="space-y-6">
          <TabsList className="bg-white border border-slate-200" data-testid="impresa-tabs">
            <TabsTrigger value="requests" data-testid="tab-requests">Richieste</TabsTrigger>
            <TabsTrigger value="company" data-testid="tab-company">Profilo</TabsTrigger>
            <TabsTrigger value="anagrafica" data-testid="tab-anagrafica">Anagrafica</TabsTrigger>
            <TabsTrigger value="documents" data-testid="tab-documents">Documenti</TabsTrigger>
            <TabsTrigger value="movements" data-testid="tab-movements">Movimenti</TabsTrigger>
            <TabsTrigger value="orders" data-testid="tab-orders">Ordini ({purchaseOrders.length})</TabsTrigger>
            <TabsTrigger value="reviews" data-testid="tab-reviews">
              Recensioni {myReviews.total > 0 && <span className="ml-2 text-xs text-slate-500">({myReviews.avg_rating}★)</span>}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="requests" className="space-y-4">
            <RequestsSplitView requests={requests} verified={verified} settings={settings} onPurchase={handlePurchase} onRequestRefund={(r) => setRefundTarget(r)} refunds={refunds} />
          </TabsContent>

          <TabsContent value="company" className="space-y-4">
            <CompanySection company={myCompany} onCreate={() => setShowCompanyModal(true)} />
          </TabsContent>

          <TabsContent value="anagrafica" className="space-y-4">
            <ImpresaAnagraficaSection onUpdate={loadData} />
          </TabsContent>

          <TabsContent value="documents" className="space-y-4">
            <DocumentsSection documents={myDocuments} required={settings?.required_documents || []} onUpload={loadData} />
          </TabsContent>

          <TabsContent value="reviews" className="space-y-4">
            <ReviewsSection reviews={myReviews} />
          </TabsContent>

          <TabsContent value="movements" className="space-y-4">
            <MovementsSection movements={movements} refunds={refunds} />
          </TabsContent>

          <TabsContent value="orders" className="space-y-4">
            <OrdersSection orders={purchaseOrders} orderRequests={orderRequests} packages={settings?.credit_packages || []} onRequestOrder={() => setShowOrderRequestModal(true)} />
          </TabsContent>
        </Tabs>
      </main>

      <CreateCompanyModal open={showCompanyModal} onClose={() => setShowCompanyModal(false)} onSuccess={() => { setShowCompanyModal(false); loadData(); }} />
      <RechargeModal open={showRechargeModal} onClose={() => setShowRechargeModal(false)} settings={settings} onSuccess={() => { setShowRechargeModal(false); loadData(); }} />
      <OrderRequestModal open={showOrderRequestModal} onClose={() => setShowOrderRequestModal(false)} packages={settings?.credit_packages || []} onSuccess={() => { setShowOrderRequestModal(false); loadData(); }} />
      <RefundModal target={refundTarget} onClose={() => setRefundTarget(null)} onSuccess={() => { setRefundTarget(null); loadData(); toast.success('Richiesta di rimborso inviata'); }} />
    </div>
  );
};

const RequestsSplitView = ({ requests, verified, settings, onPurchase, onRequestRefund, refunds }) => {
  const purchased = requests.filter(r => r.purchased_by_me);
  const general = requests.filter(r => !r.purchased_by_me);
  const [sub, setSub] = useState('purchased');
  const list = sub === 'purchased' ? purchased : general;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-2xl font-semibold text-[#0F172A]">Richieste di Ristrutturazione</h2>
        <div className="flex gap-2" data-testid="requests-subtabs">
          <Button data-testid="subtab-purchased" onClick={() => setSub('purchased')} size="sm" variant={sub === 'purchased' ? 'default' : 'outline'} className={sub === 'purchased' ? 'bg-[#EA580C] text-white' : ''}>
            Acquistate ({purchased.length})
          </Button>
          <Button data-testid="subtab-general" onClick={() => setSub('general')} size="sm" variant={sub === 'general' ? 'default' : 'outline'} className={sub === 'general' ? 'bg-[#EA580C] text-white' : ''}>
            Generali ({general.length})
          </Button>
        </div>
      </div>
      {list.length === 0 ? (
        <Card className="bg-white border border-slate-200 rounded-xl">
          <CardContent className="p-12 text-center">
            <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">{sub === 'purchased' ? 'Non hai ancora acquistato richieste' : 'Nessuna richiesta pubblica disponibile al momento'}</p>
          </CardContent>
        </Card>
      ) : (
        list.map((req) => <ImpresaRequestCard key={req.id} req={req} onPurchase={onPurchase} verified={verified} settings={settings} onRequestRefund={onRequestRefund} refunds={refunds} />)
      )}
    </div>
  );
};

const ImpresaRequestCard = ({ req, onPurchase, verified, settings, onRequestRefund, refunds }) => {
  const priceCents = (req.purchase_price_cents != null)
    ? req.purchase_price_cents
    : (settings?.request_purchase_price || 35) * 100;
  const priceEuro = (priceCents / 100).toFixed(2);
  const location = req.citta ? `${req.citta}${req.cap ? ' ' + req.cap : ''}` : (req.location_roma || '');
  const myRefund = refunds.find(r => r.request_id === req.id && r.stato !== 'rejected');
  const tipologiaLabels = { esterni: 'Esterni', appartamento: 'Appartamento', bagno: 'Bagno', cucina: 'Cucina', altro: 'Altro' };
  const tipologiaLabel = req.tipologia_lavoro ? tipologiaLabels[req.tipologia_lavoro] || req.tipologia_lavoro : null;
  
  return (
    <Card className="bg-white border border-slate-200 rounded-xl shadow-sm" data-testid={`request-card-${req.id}`}>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              {tipologiaLabel && (
                <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs font-medium" data-testid={`req-tipologia-${req.id}`}>
                  {tipologiaLabel}
                </span>
              )}
              {req.hidden_from_public && req.purchased_by_me && (
                <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded text-xs font-medium">
                  Archiviata (visibile solo a te)
                </span>
              )}
            </div>
            <CardTitle className="text-xl font-semibold text-[#0F172A]">{req.descrizione || req.titolo}</CardTitle>
            <CardDescription className="text-slate-600 mt-2">
              {location}
            </CardDescription>
          </div>
          <span className="bg-[#EA580C]/10 text-[#EA580C] px-3 py-1 rounded-full text-xs font-medium flex-shrink-0">
            {req.available_slots}/{req.max_purchases} posti
          </span>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
          <div className="bg-slate-50 rounded-md px-3 py-2">
            <span className="text-slate-500">Immobile in possesso?</span>
            <p className="font-medium text-[#0F172A]">{req.immobile_posseduto === true ? 'Sì' : req.immobile_posseduto === false ? 'No' : '—'}</p>
          </div>
          <div className="bg-slate-50 rounded-md px-3 py-2">
            <span className="text-slate-500">Progetto pronto?</span>
            <p className="font-medium text-[#0F172A]">{req.progetto_pronto === true ? 'Sì' : req.progetto_pronto === false ? 'No' : '—'}</p>
          </div>
        </div>

        {req.purchased_by_me && req.proprietario ? (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-3" data-testid={`purchased-details-${req.id}`}>
            <p className="text-sm font-semibold text-green-900 mb-2 flex items-center gap-2">
              <CheckCircle className="w-4 h-4" /> Hai acquistato questa richiesta
            </p>
            <div className="text-sm text-green-800 space-y-1">
              <p><strong>Cliente:</strong> {req.nome} {req.cognome}</p>
              <p><strong>Email:</strong> {req.email_cliente}</p>
              <p><strong>Telefono:</strong> {req.cellulare_cliente}</p>
              <p><strong>Indirizzo:</strong> {req.indirizzo}, {req.cap} {req.citta}</p>
            </div>
          </div>
        ) : (
          <div className="bg-slate-50 rounded-lg p-3 mb-3 text-sm text-slate-600">
            <p><strong>Nome:</strong> {req.nome || '****'} {req.cognome || '****'}</p>
            <p><strong>Email:</strong> {req.email_cliente || '****'}</p>
            <p><strong>Telefono:</strong> {req.cellulare_cliente || '****'}</p>
            <p className="text-xs mt-2 italic">I dati di contatto saranno rivelati dopo l'acquisto</p>
          </div>
        )}
        
        {req.purchased_by_me ? (
          <div className="flex gap-2">
            {myRefund ? (
              <span className={`inline-flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-medium ${
                myRefund.stato === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                myRefund.stato === 'approved' ? 'bg-green-100 text-green-700' :
                'bg-red-100 text-red-700'
              }`} data-testid={`refund-status-${req.id}`}>
                <AlertTriangle className="w-3 h-3" /> Rimborso: {myRefund.stato}
              </span>
            ) : (
              <Button
                data-testid={`refund-btn-${req.id}`}
                onClick={() => onRequestRefund(req)}
                size="sm"
                variant="outline"
                className="border-yellow-300 text-yellow-700 hover:bg-yellow-50"
              >
                <AlertTriangle className="w-4 h-4 mr-2" /> Segnala problema / Richiedi rimborso
              </Button>
            )}
          </div>
        ) : (
          <Button
            data-testid={`purchase-btn-${req.id}`}
            onClick={() => onPurchase(req)}
            disabled={!verified || req.available_slots <= 0}
            className="bg-[#EA580C] hover:bg-[#EA580C]/90 text-white rounded-lg"
          >
            <ShoppingCart className="w-4 h-4 mr-2" />
            {!verified ? 'Account non verificato' : req.available_slots <= 0 ? 'Posti esauriti' : `Acquista per €${priceEuro}`}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

const CompanySection = ({ company, onCreate }) => {
  if (!company) {
    return (
      <Card className="bg-white border border-slate-200 rounded-xl">
        <CardContent className="p-12 text-center">
          <Building className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-600 mb-4">Nessun profilo aziendale ancora creato</p>
          <Button data-testid="create-company-btn" onClick={onCreate} className="bg-[#EA580C] hover:bg-[#EA580C]/90 text-white rounded-lg">
            Crea Profilo Aziendale
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white border border-slate-200 rounded-xl" data-testid="company-profile">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-2xl text-[#0F172A]">{company.nome}</CardTitle>
            <CardDescription>{company.location}</CardDescription>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
            company.verificato ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
          }`}>
            {company.verificato ? 'Verificata' : 'In attesa di verifica'}
          </span>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-slate-700">{company.descrizione}</p>
      </CardContent>
    </Card>
  );
};

const ReviewsSection = ({ reviews }) => {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-[#0F172A]">Le Mie Recensioni</h2>
          <p className="text-sm text-slate-600">Cosa dicono i proprietari di te</p>
        </div>
        {reviews.total > 0 && (
          <div className="bg-white border border-slate-200 rounded-xl p-4 text-center" data-testid="reviews-avg">
            <div className="flex items-center justify-center gap-1 mb-1">
              <Star className="w-5 h-5 text-[#EA580C] fill-[#EA580C]" />
              <span className="text-2xl font-bold text-[#0F172A]">{reviews.avg_rating}</span>
            </div>
            <p className="text-xs text-slate-500">{reviews.total} recensioni</p>
          </div>
        )}
      </div>

      {reviews.reviews.length === 0 ? (
        <Card className="bg-white border border-slate-200 rounded-xl">
          <CardContent className="p-12 text-center">
            <Star className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">Nessuna recensione ricevuta ancora</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {reviews.reviews.map((r) => (
            <Card key={r.id} className="bg-white border border-slate-200 rounded-xl" data-testid={`review-${r.id}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="font-medium text-[#0F172A]">{r.proprietario_name || 'Cliente'}</p>
                    <p className="text-xs text-slate-500">Lavoro: {r.request_titolo}</p>
                  </div>
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} className={`w-4 h-4 ${n <= r.rating ? 'text-[#EA580C] fill-[#EA580C]' : 'text-slate-300'}`} />
                    ))}
                  </div>
                </div>
                <p className="text-sm text-slate-700">{r.commento}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

const DocumentsSection = ({ documents, required, onUpload }) => {
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-semibold text-[#0F172A]">Documenti Richiesti</h2>
        <p className="text-sm text-slate-600">Carica tutti i documenti per essere verificato dall'admin</p>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {required.map((docType) => {
          const uploaded = documents.find(d => d.tipo === docType);
          return <DocumentUploadCard key={docType} tipo={docType} uploaded={uploaded} onUpload={onUpload} />;
        })}
      </div>
    </div>
  );
};

const DocumentUploadCard = ({ tipo, uploaded, onUpload }) => {
  const [uploading, setUploading] = useState(false);
  const [showDatePrompt, setShowDatePrompt] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [validationDate, setValidationDate] = useState('');
  const fileInputRef = React.useRef();

  const isDurc = (tipo || '').toUpperCase() === 'DURC';
  const validityMonths = isDurc ? 6 : 12;

  const isRejected = uploaded?.approval_status === 'rejected';
  const isApproved = uploaded?.approval_status === 'approved';
  const isPending = uploaded && !isRejected && !isApproved;

  const statusBadge = (() => {
    if (!uploaded) return null;
    if (isRejected) return { text: `Rifiutato dall'admin${uploaded.approval_note ? ' — ' + uploaded.approval_note : ''}`, cls: 'bg-red-100 text-red-700' };
    if (isPending) return { text: 'In attesa di verifica admin', cls: 'bg-amber-100 text-amber-700' };
    const status = uploaded.status;
    const days = uploaded.days_left;
    if (status === 'expired_blocking') return { text: `Scaduto da ${Math.abs(days)} giorni — profilo bloccato`, cls: 'bg-red-100 text-red-700' };
    if (status === 'expired_grace') return { text: `Scaduto da ${Math.abs(days)} giorni (max 7gg)`, cls: 'bg-orange-100 text-orange-700' };
    if (status === 'expiring_soon') return { text: `Scade tra ${days} giorni`, cls: 'bg-amber-100 text-amber-700' };
    return { text: `Approvato — valido per ${days} giorni`, cls: 'bg-green-100 text-green-700' };
  })();

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPendingFile(file);
    setValidationDate(new Date().toISOString().slice(0, 10));
    setShowDatePrompt(true);
  };

  const handleConfirmUpload = async (e) => {
    e.preventDefault();
    if (!pendingFile || !validationDate) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', pendingFile);
      formData.append('tipo', tipo);
      formData.append('validation_date', validationDate);
      await axios.post(`${API}/documents/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`${tipo} caricato — valido ${validityMonths} mesi`);
      setShowDatePrompt(false);
      setPendingFile(null);
      setValidationDate('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      onUpload();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Errore nel caricamento';
      toast.error(typeof msg === 'string' ? msg : 'Errore');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card className="bg-white border border-slate-200 rounded-xl" data-testid={`doc-card-${tipo}`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-[#0F172A]">{tipo}</h3>
            <p className="text-[11px] text-slate-500 mt-0.5">Validità: {validityMonths} mesi</p>
            {uploaded && (
              <p className="text-xs text-slate-500 mt-1 truncate">{uploaded.original_filename}</p>
            )}
          </div>
          {(() => {
            if (!uploaded) return <XCircle className="w-6 h-6 text-slate-300 flex-shrink-0" data-testid={`doc-icon-${tipo}`} />;
            if (isRejected) return <XCircle className="w-6 h-6 text-red-600 flex-shrink-0" data-testid={`doc-icon-${tipo}`} />;
            if (isPending) return <Clock className="w-6 h-6 text-amber-500 flex-shrink-0" data-testid={`doc-icon-${tipo}`} />;
            return <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" data-testid={`doc-icon-${tipo}`} />;
          })()}
        </div>
        {statusBadge && (
          <span className={`inline-block text-[11px] font-medium px-2 py-0.5 rounded mb-2 ${statusBadge.cls}`} data-testid={`doc-status-${tipo}`}>
            {statusBadge.text}
          </span>
        )}
        {uploaded?.validation_date && (
          <p className="text-[11px] text-slate-500 mb-2">
            Validato: {new Date(uploaded.validation_date).toLocaleDateString('it-IT')}
            {uploaded.expiry_date && <> • Scade: {new Date(uploaded.expiry_date).toLocaleDateString('it-IT')}</>}
          </p>
        )}
        <input
          ref={fileInputRef}
          type="file"
          onChange={handleFileChange}
          className="hidden"
          data-testid={`doc-file-input-${tipo}`}
          accept=".pdf,.jpg,.jpeg,.png"
        />
        <Button
          data-testid={`doc-upload-btn-${tipo}`}
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          size="sm"
          variant={isRejected ? 'default' : 'outline'}
          className={isRejected ? 'w-full bg-red-600 hover:bg-red-700 text-white' : 'w-full'}
        >
          <Upload className="w-4 h-4 mr-2" />
          {uploading ? 'Caricamento...' : isRejected ? 'Ricarica documento' : uploaded ? 'Sostituisci' : 'Carica'}
        </Button>
      </CardContent>

      <Dialog open={showDatePrompt} onOpenChange={(o) => { if (!o) { setShowDatePrompt(false); setPendingFile(null); }}}>
        <DialogContent className="sm:max-w-md bg-white rounded-2xl border border-slate-200 shadow-2xl" data-testid={`doc-date-modal-${tipo}`}>
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-[#0F172A]">Data di validazione — {tipo}</DialogTitle>
            <DialogDescription>
              Inserisci la data in cui il documento è stato emesso/validato. La validità sarà automaticamente di <strong>{validityMonths} mesi</strong>{isDurc ? ' (DURC)' : ''}.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleConfirmUpload} className="space-y-3 mt-4">
            <div>
              <Label>Data validazione</Label>
              <Input
                data-testid={`doc-validation-date-${tipo}`}
                type="date"
                value={validationDate}
                onChange={(e) => setValidationDate(e.target.value)}
                required
                max={new Date().toISOString().slice(0, 10)}
                className="rounded-lg"
              />
            </div>
            <Button type="submit" data-testid={`doc-date-submit-${tipo}`} disabled={uploading || !validationDate} className="w-full bg-[#EA580C] hover:bg-[#EA580C]/90 text-white rounded-lg">
              {uploading ? 'Caricamento...' : 'Conferma e carica'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

const CreateCompanyModal = ({ open, onClose, onSuccess }) => {
  const [nome, setNome] = useState('');
  const [descrizione, setDescrizione] = useState('');
  const [location, setLocation] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/companies`, { nome, descrizione, location }, { withCredentials: true });
      toast.success('Profilo creato');
      onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg bg-white rounded-2xl border border-slate-200 shadow-2xl" data-testid="create-company-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold text-[#0F172A]">Crea Profilo Aziendale</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <Label>Nome Azienda</Label>
            <Input data-testid="company-name-input" value={nome} onChange={(e) => setNome(e.target.value)} required className="rounded-lg" />
          </div>
          <div>
            <Label>Descrizione</Label>
            <Textarea data-testid="company-description-input" value={descrizione} onChange={(e) => setDescrizione(e.target.value)} required rows={4} className="rounded-lg" />
          </div>
          <div>
            <Label>Zona di Operazione (Roma)</Label>
            <Input data-testid="company-location-input" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="es. Roma Nord" required className="rounded-lg" />
          </div>
          <Button type="submit" data-testid="create-company-submit-btn" disabled={loading} className="w-full bg-[#EA580C] hover:bg-[#EA580C]/90 text-white rounded-lg">
            {loading ? 'Creazione...' : 'Crea Profilo'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const RechargeModal = ({ open, onClose, settings, onSuccess }) => {
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => { if (!open) setSelectedPackage(null); }, [open]);

  const handleRecharge = async () => {
    if (!selectedPackage) { toast.error('Seleziona un pacchetto'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/impresa/order-requests`,
        { package_amount_cents: selectedPackage.amount_cents },
        { withCredentials: true }
      );
      toast.success('Richiesta ordine inviata. L\'admin emetterà il documento con le istruzioni di bonifico via email.');
      if (onSuccess) onSuccess();
      else onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg bg-white rounded-2xl border border-slate-200 shadow-2xl" data-testid="recharge-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold text-[#0F172A]">Acquista Crediti</DialogTitle>
          <DialogDescription>Pagamento tramite bonifico bancario. Riceverai le istruzioni via email una volta che l'admin avrà emesso l'ordine.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div className="grid grid-cols-3 gap-3">
            {(settings?.credit_packages || []).map((pkg) => {
              const eur = (pkg.amount_cents || 0) / 100;
              return (
                <button
                  key={pkg.amount_cents}
                  type="button"
                  data-testid={`recharge-pkg-${pkg.amount_cents}`}
                  onClick={() => setSelectedPackage(pkg)}
                  className={`relative border-2 rounded-lg p-4 text-center transition-all ${
                    selectedPackage?.amount_cents === pkg.amount_cents
                      ? 'border-[#EA580C] bg-[#EA580C]/10'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  {pkg.bonus_pct > 0 && (
                    <span className="absolute -top-2 -right-2 bg-green-600 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                      +{pkg.bonus_pct}%
                    </span>
                  )}
                  <p className="text-2xl font-bold text-[#0F172A]">€{eur}</p>
                  <p className="text-xs text-slate-500 mt-1">{pkg.credits} crediti</p>
                </button>
              );
            })}
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900">
            💡 Una volta confermata la richiesta, l'admin emetterà l'ordine e riceverai una <strong>email di noreply@ristrutturacasaroma.it</strong> con coordinate bancarie e numero d'ordine da usare come causale.
          </div>
          <Button data-testid="recharge-confirm-btn" onClick={handleRecharge} disabled={!selectedPackage || loading} className="w-full bg-[#EA580C] hover:bg-[#EA580C]/90 text-white rounded-lg">
            {loading ? 'Invio...' : selectedPackage ? `Richiedi ordine €${(selectedPackage.amount_cents || 0) / 100}` : 'Seleziona un pacchetto'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const MovementsSection = ({ movements, refunds }) => {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-[#0F172A]">Movimenti</h2>
        <p className="text-sm text-slate-600">Storico delle ricariche di crediti e degli acquisti di contatti</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card className="bg-white border border-slate-200 rounded-xl" data-testid="recharges-section">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><TrendingUp className="w-5 h-5 text-green-600" /> Ricariche Crediti</CardTitle>
          </CardHeader>
          <CardContent>
            {movements.recharges.length === 0 ? (
              <p className="text-sm text-slate-500">Nessuna ricarica</p>
            ) : (
              <div className="space-y-2">
                {movements.recharges.map((r) => (
                  <div key={r.id} className="flex items-center justify-between border-b border-slate-100 pb-2 last:border-0">
                    <div>
                      <p className="text-sm font-medium text-[#0F172A]">€{r.amount} ({r.payment_method === 'card' ? 'Carta' : 'Bonifico'})</p>
                      <p className="text-xs text-slate-500">{new Date(r.created_at).toLocaleString('it-IT')}</p>
                      {(r.metadata?.credits_cents || r.metadata?.credits) && (
                        <p className="text-xs text-green-600">
                          +€{((parseInt(r.metadata?.credits_cents || (parseInt(r.metadata?.credits || 0) * 100)) || 0) / 100).toFixed(2)} di crediti
                        </p>
                      )}
                    </div>
                    <span className={`text-xs px-2 py-1 rounded ${
                      r.payment_status === 'paid' ? 'bg-green-100 text-green-700' :
                      r.payment_status === 'rejected' ? 'bg-red-100 text-red-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>{r.payment_status}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-200 rounded-xl" data-testid="purchases-section">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><ShoppingCart className="w-5 h-5 text-[#EA580C]" /> Contatti Acquistati</CardTitle>
          </CardHeader>
          <CardContent>
            {movements.purchases.length === 0 ? (
              <p className="text-sm text-slate-500">Nessun acquisto</p>
            ) : (
              <div className="space-y-2">
                {movements.purchases.map((p) => (
                  <div key={p.id} className="border-b border-slate-100 pb-2 last:border-0">
                    <p className="text-sm font-medium text-[#0F172A]">{p.request_descrizione || p.request_titolo || 'Richiesta'}</p>
                    <div className="flex items-center justify-between mt-1">
                      <p className="text-xs text-slate-500">{new Date(p.purchased_at).toLocaleString('it-IT')}</p>
                      <p className="text-xs text-red-600">-€{(((p.credits_spent_cents != null ? p.credits_spent_cents : (p.credits_spent || 0) * 100)) / 100).toFixed(2)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {refunds.length > 0 && (
        <Card className="bg-white border border-slate-200 rounded-xl" data-testid="refunds-section">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-yellow-600" /> Richieste di Rimborso</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {refunds.map((rf) => (
                <div key={rf.id} className="flex items-start justify-between border-b border-slate-100 pb-2 last:border-0">
                  <div>
                    <p className="text-sm text-[#0F172A]">{rf.motivo}</p>
                    <p className="text-xs text-slate-500">{new Date(rf.created_at).toLocaleString('it-IT')}</p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded ${
                    rf.stato === 'approved' ? 'bg-green-100 text-green-700' :
                    rf.stato === 'rejected' ? 'bg-red-100 text-red-700' :
                    'bg-yellow-100 text-yellow-700'
                  }`}>{rf.stato}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const OrdersSection = ({ orders, orderRequests, packages, onRequestOrder }) => {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-semibold text-[#0F172A]">Ordini di Acquisto</h2>
          <p className="text-sm text-slate-600">Richiedi un ordine per pagare con bonifico bancario.</p>
        </div>
        <Button data-testid="request-order-btn" onClick={onRequestOrder} disabled={!packages.length} className="bg-[#EA580C] hover:bg-[#EA580C]/90 text-white rounded-lg">
          <ScrollText className="w-4 h-4 mr-2" /> Richiedi emissione ordine
        </Button>
      </div>

      {orderRequests.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Le tue richieste</p>
          {orderRequests.map((or) => (
            <Card key={or.id} className="bg-white border border-slate-200 rounded-xl" data-testid={`order-request-${or.id}`}>
              <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[#0F172A]">
                    Pacchetto: <strong>{or.package_credits} crediti</strong> {or.package_bonus_pct ? `(bonus ${or.package_bonus_pct}%)` : ''} • €{(or.package_amount_cents / 100).toFixed(2)}
                  </p>
                  {or.order_number && <p className="font-mono text-xs text-[#EA580C]">{or.order_number}</p>}
                  <p className="text-xs text-slate-500">{new Date(or.created_at).toLocaleString('it-IT')}</p>
                  {or.stato === 'emesso' && or.bank_info && (
                    <div className="mt-2 bg-blue-50 border border-blue-200 rounded p-2 text-xs text-blue-900 whitespace-pre-line" data-testid={`order-bank-info-${or.id}`}>
                      <strong>Istruzioni bonifico:</strong>
                      {'\n'}{or.bank_info}
                      {or.transaction_id && (<>{'\n'}Causale: {or.transaction_id}</>)}
                    </div>
                  )}
                </div>
                <span className={`text-xs px-2 py-1 rounded font-medium ${
                  or.stato === 'richiesto' ? 'bg-yellow-100 text-yellow-700' :
                  or.stato === 'emesso' ? 'bg-blue-100 text-blue-700' :
                  or.stato === 'pagato' ? 'bg-green-100 text-green-700' :
                  'bg-red-100 text-red-700'
                }`}>{or.stato}</span>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <div className="space-y-2">
        <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Ordini emessi</p>
        {orders.length === 0 ? (
          <Card className="bg-white border border-slate-200 rounded-xl">
            <CardContent className="p-12 text-center">
              <ScrollText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">Nessun ordine emesso</p>
            </CardContent>
          </Card>
        ) : (
          orders.map((o) => (
            <Card key={o.id} className="bg-white border border-slate-200 rounded-xl" data-testid={`order-${o.id}`}>
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="font-mono text-sm font-semibold text-[#EA580C]">{o.order_number}</p>
                  <p className="text-sm text-[#0F172A]">{o.descrizione}</p>
                  <p className="text-xs text-slate-500">{new Date(o.created_at).toLocaleString('it-IT')}</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-[#0F172A]">€{o.importo}</p>
                  <span className="text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded">{o.stato}</span>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

const OrderRequestModal = ({ open, onClose, packages, onSuccess }) => {
  const [selected, setSelected] = useState('');
  const [loading, setLoading] = useState(false);

  React.useEffect(() => { if (!open) setSelected(''); }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selected) { toast.error('Seleziona un pacchetto'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/impresa/order-requests`, { package_amount_cents: parseInt(selected) }, { withCredentials: true });
      toast.success('Richiesta ordine inviata. L\'admin emetterà l\'ordine con le istruzioni di bonifico.');
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-white rounded-2xl border border-slate-200 shadow-2xl" data-testid="order-request-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold text-[#0F172A]">Richiedi Emissione Ordine</DialogTitle>
          <DialogDescription>Seleziona il pacchetto crediti. Riceverai le istruzioni di bonifico una volta che l'admin avrà emesso l'ordine.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3 mt-4">
          {packages.map((p) => {
            const eur = (p.amount_cents / 100).toFixed(2);
            const isSel = selected === String(p.amount_cents);
            return (
              <button
                key={p.amount_cents}
                type="button"
                data-testid={`order-pkg-${p.amount_cents}`}
                onClick={() => setSelected(String(p.amount_cents))}
                className={`w-full border-2 rounded-lg p-4 text-left transition-all ${isSel ? 'border-[#EA580C] bg-[#EA580C]/5' : 'border-slate-200 hover:border-slate-300'}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-[#0F172A]">{p.credits} crediti</p>
                    {p.bonus_pct > 0 && <p className="text-xs text-emerald-600">+{p.bonus_pct}% bonus</p>}
                  </div>
                  <p className="text-xl font-bold text-[#0F172A]">€{eur}</p>
                </div>
              </button>
            );
          })}
          <Button type="submit" data-testid="order-request-submit" disabled={loading || !selected} className="w-full bg-[#EA580C] hover:bg-[#EA580C]/90 text-white rounded-lg">
            {loading ? 'Invio...' : 'Invia richiesta ordine'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const RefundModal = ({ target, onClose, onSuccess }) => {
  const [motivo, setMotivo] = useState('');
  const [loading, setLoading] = useState(false);

  React.useEffect(() => { if (target) setMotivo(''); }, [target]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!target) return;
    setLoading(true);
    try {
      await axios.post(`${API}/refund-requests`, { request_id: target.id, motivo }, { withCredentials: true });
      onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg bg-white rounded-2xl border border-slate-200 shadow-2xl" data-testid="refund-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold text-[#0F172A]">Richiedi Rimborso</DialogTitle>
          <DialogDescription>Spiega il motivo (es. cliente non risponde, dati non veritieri). L'admin valuterà la richiesta.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <Label>Motivo</Label>
            <Textarea data-testid="refund-motivo-input" value={motivo} onChange={(e) => setMotivo(e.target.value)} required rows={5} placeholder="Descrivi il problema riscontrato..." className="rounded-lg" />
          </div>
          <Button type="submit" data-testid="submit-refund-btn" disabled={loading} className="w-full bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg">
            {loading ? 'Invio...' : 'Invia richiesta di rimborso'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const ImpresaAnagraficaSection = ({ onUpdate }) => {
  const [profile, setProfile] = useState(null);
  const [ragioneSociale, setRagioneSociale] = useState('');
  const [referente, setReferente] = useState('');
  const [partitaIva, setPartitaIva] = useState('');
  const [codiceFiscale, setCodiceFiscale] = useState('');
  const [sedeLegale, setSedeLegale] = useState('');
  const [phone, setPhone] = useState('');
  const [pec, setPec] = useState('');
  const [sitoWeb, setSitoWeb] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/impresa/me`, { withCredentials: true });
      setProfile(data);
      setRagioneSociale(data.ragione_sociale || '');
      setReferente(data.referente || data.name || '');
      setPartitaIva(data.partita_iva || '');
      setCodiceFiscale(data.codice_fiscale || '');
      setSedeLegale(data.sede_legale || '');
      setPhone(data.phone || '');
      setPec(data.pec || '');
      setSitoWeb(data.sito_web || '');
    } catch (e) {
      toast.error('Errore caricamento anagrafica');
    } finally { setLoading(false); }
  };
  React.useEffect(() => { load(); }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/impresa/me`, {
        ragione_sociale: ragioneSociale,
        referente,
        partita_iva: partitaIva,
        codice_fiscale: codiceFiscale,
        sede_legale: sedeLegale,
        phone,
        pec,
        sito_web: sitoWeb,
      }, { withCredentials: true });
      toast.success('Anagrafica aggiornata');
      await load();
      if (onUpdate) onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    } finally { setSaving(false); }
  };

  if (loading) return <p className="text-slate-500">Caricamento...</p>;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-semibold text-[#0F172A]">Anagrafica Impresa</h2>
        <p className="text-sm text-slate-600">Aggiorna i dati di registrazione della tua impresa. Modifiche significative (P.IVA/CF) potrebbero richiedere una nuova verifica admin.</p>
      </div>
      <Card className="bg-white border border-slate-200 rounded-xl" data-testid="impresa-anagrafica-form">
        <CardContent className="p-6">
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <Label>Ragione Sociale</Label>
              <Input data-testid="impresa-ragione-input" value={ragioneSociale} onChange={(e) => setRagioneSociale(e.target.value)} required className="rounded-lg" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Partita IVA</Label>
                <Input data-testid="impresa-piva-input" value={partitaIva} onChange={(e) => setPartitaIva(e.target.value.toUpperCase())} className="rounded-lg font-mono" />
              </div>
              <div>
                <Label>Codice Fiscale</Label>
                <Input data-testid="impresa-cf-input" value={codiceFiscale} onChange={(e) => setCodiceFiscale(e.target.value.toUpperCase())} className="rounded-lg font-mono" />
              </div>
            </div>
            <div>
              <Label>Sede Legale</Label>
              <Input data-testid="impresa-sede-input" value={sedeLegale} onChange={(e) => setSedeLegale(e.target.value)} placeholder="Via Roma 1, 00100 Roma" className="rounded-lg" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Referente</Label>
                <Input data-testid="impresa-referente-input" value={referente} onChange={(e) => setReferente(e.target.value)} className="rounded-lg" />
              </div>
              <div>
                <Label>Telefono</Label>
                <Input data-testid="impresa-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} className="rounded-lg" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>PEC</Label>
                <Input data-testid="impresa-pec-input" type="email" value={pec} onChange={(e) => setPec(e.target.value)} placeholder="impresa@pec.it" className="rounded-lg" />
              </div>
              <div>
                <Label>Sito Web</Label>
                <Input data-testid="impresa-sito-input" value={sitoWeb} onChange={(e) => setSitoWeb(e.target.value)} placeholder="https://..." className="rounded-lg" />
              </div>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-600">
              📧 Email account: <strong>{profile?.email}</strong> (modificabile da Impostazioni → Cambia email)<br/>
              📅 Registrata il: <strong>{profile?.created_at ? new Date(profile.created_at).toLocaleDateString('it-IT') : '—'}</strong>
            </div>
            <Button type="submit" data-testid="impresa-anagrafica-save" disabled={saving} className="bg-[#EA580C] hover:bg-[#EA580C]/90 text-white rounded-lg">
              {saving ? 'Salvataggio...' : 'Salva anagrafica'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ImpresaDashboard;
